import { omit } from 'lodash';
import withQuery from 'with-query';

import YCApi from './ycApi';

export const CACHE_KEYS = {
  TOKEN: 'authToken',
  ID: 'authId',
  AFTER_SIGNUP_LINK: 'afterSignupLink'
};

export default class CandidateService {
  constructor({ serializer, cache, isMobileApp = false }) {
    this.cache = cache;

    this.isMobileApp = isMobileApp;

    this.serializer = serializer;
  }

  _submitAppToken = () => {
    if (!window || !window.ReactNativeWebView) return;

    const message = JSON.stringify({ partialAuthToken: this.authToken });

    window.ReactNativeWebView.postMessage(message);
  };

  /* Getters and Setters */
  get api() {
    return new YCApi({
      authToken: this.authToken
    });
  }

  get authToken() {
    return this.cache.get(CACHE_KEYS.TOKEN);
  }

  set authToken(token) {
    this.cache.set(CACHE_KEYS.TOKEN, token);
  }

  get isAuthenticated() {
    return !!this.authToken;
  }

  get authId() {
    return this.cache.get(CACHE_KEYS.ID);
  }

  set authId(id) {
    this.cache.set(CACHE_KEYS.ID, id);
  }

  get afterLink() {
    return this.cache.get(CACHE_KEYS.AFTER_SIGNUP_LINK);
  }

  set afterLink(link) {
    this.cache.set(CACHE_KEYS.AFTER_SIGNUP_LINK, link);
  }

  get urlAddition() {
    return withQuery(this.afterLink, {
      candidate_signup_v2: 'true'
    });
  }

  _sanitizeValues(values, omitPassword = false) {
    const serializedValues = this.serializer(values);
    return omitPassword
      ? omit(serializedValues, ['password'])
      : serializedValues;
  }

  _updateMeta({ authToken, id, link }) {
    this.authToken = authToken;
    this.authId = id;
    this.afterLink = link;
  }

  /* Public */
  async save(values) {
    const hasToken = !!this.authToken;
    const sanitizedValues = this._sanitizeValues(values, hasToken);

    const updateCandidateResponse = hasToken
      ? await this.api.updatePartialCandidate(this.authId, sanitizedValues)
      : await this.api.submitPartialCandidate(sanitizedValues);

    const { meta, links, data } = updateCandidateResponse.data;

    if (meta) {
      this._updateMeta({
        authToken: meta.authentication_token,
        id: data.id,
        link: links.after_signup_link
      });

      if (this.isMobileApp) {
        this._submitAppToken();
      }
    }

    if (values.upload_cv) {
      await this.api.submitFile(values.upload_cv);
    }
    return updateCandidateResponse.data;
  }

  fetchNlAddress(address) {
    return this.api
      .fetchNetherlandsAddress(address)
      .then(({ data }) =>
        data !== null ? Promise.resolve(data.zipcode) : Promise.reject()
      );
  }

  fetchEducationsList(manualInput, selectedEduTypeId) {
    return this.api
      .fetchEducationsList(manualInput, selectedEduTypeId)
      .then(({ data }) =>
        data !== null ? Promise.resolve(data.data) : Promise.reject()
      );
  }
}
